import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import useAuthStore from "../../store/authStore";

export default function Settings() {
  const navigate = useNavigate();
  const { seekerUser, setSeekerAuth, clearSeekerAuth } = useAuthStore();

  const [profile, setProfile] = useState({
    full_name: seekerUser?.full_name ?? "",
    email: seekerUser?.email ?? "",
  });
  const [password, setPassword] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [profileMsg, setProfileMsg] = useState(null);
  const [passwordMsg, setPasswordMsg] = useState(null);
  const [profileErr, setProfileErr] = useState(null);
  const [passwordErr, setPasswordErr] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [arenaProfile, setArenaProfile] = useState(null);
  const [arenaDirty, setArenaDirty] = useState(false);
  const [savingArena, setSavingArena] = useState(false);
  const [arenaMsg, setArenaMsg] = useState(null);
  const [localArena, setLocalArena] = useState(null);
  const [myProcesses, setMyProcesses] = useState([]);

  useEffect(() => {
    api
      .get("/arena/my-processes")
      .then(({ data }) => setMyProcesses(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/arena/profile")
      .then(({ data }) => {
        setArenaProfile(data);
        setLocalArena(data);
      })
      .catch(() => {});
  }, []);

  // Track dirty state
  useEffect(() => {
    if (!arenaProfile || !localArena) return;
    const fieldsOnly = (p) => ({
      anonymous_handle: p.anonymous_handle,
      location_preference: p.location_preference,
      availability: p.availability,
      open_to_relocation: p.open_to_relocation,
      salary_min: p.salary_min,
      salary_max: p.salary_max,
      salary_currency: p.salary_currency,
      skills_snapshot: p.skills_snapshot,
    });
    const changed =
      JSON.stringify(fieldsOnly(arenaProfile)) !==
      JSON.stringify(fieldsOnly(localArena));
    setArenaDirty(changed);
  }, [localArena, arenaProfile]);

  const handleArenaToggle = async (patch) => {
    try {
      const { data } = await api.patch("/arena/profile", patch);
      setArenaProfile(data);
      setLocalArena(data);
    } catch (err) {
      alert(err.response?.data?.detail ?? err.message);
    }
  };

  const handleArenaSave = async () => {
    setSavingArena(true);
    setArenaMsg(null);
    try {
      const { data } = await api.patch("/arena/profile", {
        anonymous_handle: localArena.anonymous_handle,
        location_preference: localArena.location_preference,
        availability: localArena.availability,
        open_to_relocation: localArena.open_to_relocation,
        salary_min: localArena.salary_min,
        salary_max: localArena.salary_max,
        salary_currency: localArena.salary_currency,
        skills_snapshot: localArena.skills_snapshot,
      });
      setArenaProfile(data);
      setLocalArena(data);
      setArenaDirty(false);
      setArenaMsg("Arena profile saved.");
      setTimeout(() => setArenaMsg(null), 3000);
    } catch (err) {
      alert(err.response?.data?.detail ?? err.message);
    } finally {
      setSavingArena(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileMsg(null);
    setProfileErr(null);
    try {
      const { data } = await api.patch("/users/me", profile);
      setSeekerAuth(useAuthStore.getState().seekerToken, data);
      setProfileMsg("Profile updated successfully.");
    } catch (err) {
      setProfileErr(err.response?.data?.detail ?? err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    setPasswordMsg(null);
    setPasswordErr(null);
    if (password.new_password !== password.confirm_password) {
      setPasswordErr("New passwords do not match.");
      return;
    }
    if (password.new_password.length < 8) {
      setPasswordErr("New password must be at least 8 characters.");
      return;
    }
    setSavingPassword(true);
    try {
      await api.post("/users/me/change-password", {
        current_password: password.current_password,
        new_password: password.new_password,
      });
      setPasswordMsg("Password updated successfully.");
      setPassword({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (err) {
      setPasswordErr(err.response?.data?.detail ?? err.message);
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await api.delete("/users/me");
      clearSeekerAuth();
      navigate("/");
    } catch (err) {
      alert(err.response?.data?.detail ?? err.message);
    }
  };

  const inputClass =
    "border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark w-full";
  const labelClass =
    "block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5";

  const updateLocal = (field, value) => {
    setLocalArena((prev) => ({ ...prev, [field]: value }));
  };

  // The arena is anonymous by design, but the handle is free text, so someone
  // can put their own email or phone in it without realising they have undone
  // that. Warn rather than block, since a candidate may genuinely want to be
  // reachable.
  const CONTACT_PATTERN =
    /([\w.+-]+@[\w-]+\.[\w.]+)|(\+?\d[\d\s().-]{7,}\d)/;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <NavBar currentPage="settings" />

      <main className="flex-1 max-w-2xl mx-auto px-6 py-10 w-full">
        <h1 className="text-2xl font-bold mb-8">Account settings</h1>

        {/* Profile section */}
        <section className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-200 dark:border-white/10 p-6 mb-6">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-5">
            Profile
          </h2>
          {profileMsg && (
            <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-4 py-3">
              <p className="text-green-700 dark:text-green-400 text-sm">
                {profileMsg}
              </p>
            </div>
          )}
          {profileErr && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
              <p className="text-red-600 dark:text-red-400 text-sm">
                {profileErr}
              </p>
            </div>
          )}
          <form onSubmit={handleProfileSave} className="flex flex-col gap-4">
            <div>
              <label className={labelClass}>Full name</label>
              <input
                value={profile.full_name}
                onChange={(e) =>
                  setProfile({ ...profile, full_name: e.target.value })
                }
                placeholder="Your full name"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) =>
                  setProfile({ ...profile, email: e.target.value })
                }
                placeholder="your@email.com"
                className={inputClass}
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={savingProfile}
                className="bg-brand-violet text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
              >
                {savingProfile ? "Saving..." : "Save profile"}
              </button>
            </div>
          </form>
        </section>

        {/* Password section */}
        <section className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-200 dark:border-white/10 p-6 mb-6">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-5">
            Change password
          </h2>
          {passwordMsg && (
            <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-4 py-3">
              <p className="text-green-700 dark:text-green-400 text-sm">
                {passwordMsg}
              </p>
            </div>
          )}
          {passwordErr && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
              <p className="text-red-600 dark:text-red-400 text-sm">
                {passwordErr}
              </p>
            </div>
          )}
          <form onSubmit={handlePasswordSave} className="flex flex-col gap-4">
            <div>
              <label className={labelClass}>Current password</label>
              <input
                type="password"
                value={password.current_password}
                onChange={(e) =>
                  setPassword({ ...password, current_password: e.target.value })
                }
                placeholder="Enter current password"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>New password</label>
              <input
                type="password"
                value={password.new_password}
                onChange={(e) =>
                  setPassword({ ...password, new_password: e.target.value })
                }
                placeholder="Min 8 characters"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Confirm new password</label>
              <input
                type="password"
                value={password.confirm_password}
                onChange={(e) =>
                  setPassword({ ...password, confirm_password: e.target.value })
                }
                placeholder="Repeat new password"
                className={inputClass}
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={savingPassword}
                className="bg-brand-violet text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
              >
                {savingPassword ? "Updating..." : "Update password"}
              </button>
            </div>
          </form>
        </section>

        {/* Arena section */}
        <section className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-200 dark:border-white/10 p-6 mb-6">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-1">
            Candidate Arena
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            Let employers and recruitment agencies find you anonymously. Your
            name and gender are never shown.
          </p>

          {arenaMsg && (
            <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-4 py-3">
              <p className="text-green-700 dark:text-green-400 text-sm">
                {arenaMsg}
              </p>
            </div>
          )}

          {localArena && (
            <div className="flex flex-col gap-4">
              {/* Toggle — direct employer visibility */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Discoverable by employers
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Companies hiring directly — only skills, availability and
                    salary range shown
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={localArena.is_active}
                      onChange={(e) => {
                        updateLocal("is_active", e.target.checked);
                        handleArenaToggle({ is_active: e.target.checked });
                      }}
                    />
                    <div className="w-10 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:bg-brand-violet transition" />
                    <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition peer-checked:translate-x-4" />
                  </div>
                </label>
              </div>

              {arenaProfile?.profile_view_count > 0 && (
                <div className="flex items-center gap-2 py-3 border-t border-gray-100 dark:border-white/10">
                  <div className="w-8 h-8 rounded-lg bg-brand-violet/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-brand-violet dark:text-brand-violet-on-dark">
                      {arenaProfile.profile_view_count}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Profile views
                    </p>
                    <p className="text-xs text-gray-400">
                      Employers and agencies who have viewed your resume
                    </p>
                  </div>
                </div>
              )}

              {/* Toggle — recruitment agency visibility */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-white/10">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Discoverable by recruitment agencies
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Headhunters and staffing firms — same anonymity as above,
                    separate opt-in
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={localArena.visible_to_agencies ?? false}
                      onChange={(e) => {
                        updateLocal("visible_to_agencies", e.target.checked);
                        handleArenaToggle({
                          visible_to_agencies: e.target.checked,
                        });
                      }}
                    />
                    <div className="w-10 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:bg-brand-violet transition" />
                    <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition peer-checked:translate-x-4" />
                  </div>
                </label>
              </div>

              {(localArena.is_active || localArena.visible_to_agencies) && (
                <div className="flex flex-col gap-3 pt-3 border-t border-gray-100 dark:border-white/10">
                  <div>
                    <label className={labelClass}>
                      Your handle (shown to employers and agencies)
                    </label>
                    <input
                      value={localArena.anonymous_handle ?? ""}
                      onChange={(e) =>
                        updateLocal("anonymous_handle", e.target.value)
                      }
                      placeholder="e.g. Senior Frontend Engineer · Nordics · 6yr"
                      className={inputClass}
                    />
                    {CONTACT_PATTERN.test(localArena.anonymous_handle ?? "") && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        This looks like it contains an email address or phone
                        number. Employers will see it, which removes the
                        anonymity the arena is meant to give you.
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      Describe yourself without name or gender
                    </p>
                  </div>

                  <div>
                    <label className={labelClass}>Location preference</label>
                    <input
                      value={localArena.location_preference ?? ""}
                      onChange={(e) =>
                        updateLocal("location_preference", e.target.value)
                      }
                      placeholder="e.g. Nordics, Remote, EMEA"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Availability</label>
                    <select
                      value={localArena.availability ?? ""}
                      onChange={(e) =>
                        updateLocal("availability", e.target.value)
                      }
                      className={inputClass}
                    >
                      <option value="">Select...</option>
                      <option value="immediate">Available immediately</option>
                      <option value="one_month">Available in 1 month</option>
                      <option value="three_months">
                        Available in 3 months
                      </option>
                      <option value="not_looking">Not actively looking</option>
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Salary expectation</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={localArena.salary_min ?? ""}
                        onChange={(e) =>
                          updateLocal(
                            "salary_min",
                            parseInt(e.target.value) || null,
                          )
                        }
                        placeholder="Min"
                        className={inputClass}
                      />
                      <input
                        type="number"
                        value={localArena.salary_max ?? ""}
                        onChange={(e) =>
                          updateLocal(
                            "salary_max",
                            parseInt(e.target.value) || null,
                          )
                        }
                        placeholder="Max"
                        className={inputClass}
                      />
                      {/* fix 5: currency selector */}
                      <select
                        value={localArena.salary_currency ?? "EUR"}
                        onChange={(e) =>
                          updateLocal("salary_currency", e.target.value)
                        }
                        className={`${inputClass} w-28`}
                      >
                        {["EUR", "SEK", "GBP", "USD", "NOK", "DKK"].map((c) => (
                          <option key={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="relocation"
                      checked={localArena.open_to_relocation ?? false}
                      onChange={(e) =>
                        updateLocal("open_to_relocation", e.target.checked)
                      }
                      className="rounded border-gray-300 text-brand-violet dark:text-brand-violet-on-dark focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
                    />
                    <label
                      htmlFor="relocation"
                      className="text-sm text-gray-600 dark:text-gray-300 cursor-pointer"
                    >
                      Open to relocation
                    </label>
                  </div>

                  {/* fix 1: save button */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-white/10">
                    {arenaDirty ? (
                      <p className="text-xs text-amber-500">Unsaved changes</p>
                    ) : (
                      <p className="text-xs text-gray-400">All changes saved</p>
                    )}
                    <button
                      onClick={handleArenaSave}
                      disabled={savingArena || !arenaDirty}
                      className="bg-brand-violet text-white px-5 py-2 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-40 transition"
                    >
                      {savingArena ? "Saving..." : "Save arena profile"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Active processes */}
        {myProcesses.length > 0 && (
          <section className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-200 dark:border-white/10 p-6 mb-6">
            <h2 className="font-semibold text-gray-900 dark:text-white mb-1">
              Active hiring processes
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              Employers and agencies who have connected to your profile and are
              actively managing your process.
            </p>
            <div className="flex flex-col gap-3">
              {myProcesses.map((p) => (
                <div
                  key={p.integration_id}
                  className="flex items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-brand-dark-bg rounded-xl"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {p.job_title}
                      </p>
                      <span className="text-gray-400 text-xs">·</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {p.company}
                      </p>
                      {p.is_agency && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400">
                          Agency
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-brand-violet/10 text-brand-violet dark:text-brand-violet-on-dark">
                        {p.current_status}
                      </span>
                      {p.last_update_at && (
                        <span className="text-xs text-gray-400">
                          Last update:{" "}
                          {new Date(p.last_update_at).toLocaleDateString(
                            "en-SE",
                          )}
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        {p.push_count} update{p.push_count !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      navigate(
                        `/app/dashboard?open=${p.application_id}&tab=Status`,
                      )
                    }
                    className="text-xs border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 px-3 py-1.5 rounded-lg hover:border-brand-violet dark:hover:border-brand-violet-on-dark hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition flex-shrink-0"
                  >
                    View status
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Subscription */}
        <section className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-200 dark:border-white/10 p-6 mb-6">          <h2 className="font-semibold text-gray-900 dark:text-white mb-1">
            Subscription
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            Your Joborigo plan.
          </p>
          <div className="flex items-center gap-3">
            <span className="bg-brand-violet text-white text-xs font-semibold px-3 py-1 rounded-full">
              Free
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Joborigo is free for job seekers, forever.
            </span>
          </div>
        </section>

        {/* Danger zone */}
        <section className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-red-200 dark:border-red-900/50 p-6">
          <h2 className="font-semibold text-red-600 dark:text-red-400 mb-2">
            Danger zone
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Permanently delete your account and all your data. This cannot be
            undone.
          </p>
          {!showDelete ? (
            <button
              onClick={() => setShowDelete(true)}
              className="border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 px-5 py-2 rounded-lg text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition"
            >
              Delete my account
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                Are you sure?
              </p>
              <button
                onClick={handleDeleteAccount}
                className="bg-red-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition"
              >
                Yes, delete everything
              </button>
              <button
                onClick={() => setShowDelete(false)}
                className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition"
              >
                Cancel
              </button>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
